import React, { useState } from 'react';
import { ArrowLeft, Send, Loader2, CheckCircle } from 'lucide-react';
interface DataSourceAgentProps {
  sourceType: 'knowledge-base' | 'api' | 'file-upload';
  onBack: () => void;
}
const DataSourceAgent: React.FC<DataSourceAgentProps> = ({
  sourceType,
  onBack
}) => {
  const [messages, setMessages] = useState<Array<{
    role: 'user' | 'agent';
    content: string;
    status?: string;
  }>>([{
    role: 'agent',
    content: getInitialMessage(sourceType)
  }]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  function getInitialMessage(type: string): string {
    switch (type) {
      case 'knowledge-base':
        return "I'll help you create a new knowledge base for your professional growth system. What would you like to name this knowledge base, and what kind of information should it contain?";
      case 'api':
        return "I'll guide you through connecting to an external API data source. What API would you like to connect to? Please provide the API name or URL if you have it.";
      case 'file-upload':
        return "I'll help you upload and process documents to create a new data source. What types of documents would you like to upload, and what information do they contain?";
      default:
        return "I'll help you add a new data source to your professional growth system. How can I assist you today?";
    }
  }
  const handleSendMessage = () => {
    if (!input.trim()) return;
    // Add user message
    setMessages(prev => [...prev, {
      role: 'user',
      content: input
    }]);
    setInput('');
    setIsProcessing(true);
    // Simulate agent processing
    setTimeout(() => {
      let response = '';
      // Simulate agent responses based on source type and conversation context
      if (sourceType === 'knowledge-base') {
        if (messages.length === 1) {
          response = "Great! I'll help you set up a knowledge base with that information. Would you like to seed it with existing data, or start from scratch? If you have existing data, what format is it in (CSV, JSON, documents)?";
        } else {
          response = "I've started creating your knowledge base. Would you like me to configure any specific access permissions or integration settings for this knowledge base?";
        }
      } else if (sourceType === 'api') {
        if (messages.length === 1) {
          response = "Thanks for that information. To connect to this API, I'll need the following details:\n\n1. API endpoint URL\n2. Authentication method (API key, OAuth, etc.)\n3. Data format (JSON, XML, etc.)\n\nDo you have this information available?";
        } else {
          response = "I've recorded those API details. Would you like me to test the connection now, or would you like to configure how frequently this data source should be synced?";
        }
      } else if (sourceType === 'file-upload') {
        if (messages.length === 1) {
          response = "I understand the types of documents you want to upload. When you're ready, you can upload the files here, and I'll process them to extract the relevant information. Would you like me to apply any specific data processing or transformation to these documents?";
        } else {
          response = "I've prepared the file upload system. Once your files are processed, would you like me to set up any automatic updates or periodic checks for new documents?";
        }
      }
      setMessages(prev => [...prev, {
        role: 'agent',
        content: response
      }]);
      setIsProcessing(false);
    }, 1500);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <div className="flex flex-col h-full" data-id="element-438">
      <div className="flex items-center mb-4" data-id="element-439">
        <button onClick={onBack} className="mr-2 p-1 rounded-md hover:bg-gray-100" data-id="element-440">
          <ArrowLeft className="w-5 h-5" data-id="element-441" />
        </button>
        <h3 className="text-lg font-medium" data-id="element-442">
          {sourceType === 'knowledge-base' ? 'Knowledge Base Setup' : sourceType === 'api' ? 'API Connection' : 'File Upload'}
        </h3>
      </div>
      <div className="flex-1 overflow-auto mb-4 border border-gray-200 rounded-lg bg-gray-50 p-4" data-id="element-443">
        <div className="space-y-4" data-id="element-444">
          {messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-445">
              <div className={`max-w-[80%] p-3 rounded-lg ${message.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`} data-id="element-446">
                <p className="whitespace-pre-wrap" data-id="element-447">{message.content}</p>
                {message.status && <div className="mt-2 flex items-center text-xs text-gray-400" data-id="element-448">
                    {message.status === 'processing' ? <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" data-id="element-449" />
                        Processing...
                      </> : <>
                        <CheckCircle className="w-3 h-3 mr-1" data-id="element-450" />
                        Complete
                      </>}
                  </div>}
              </div>
            </div>)}
          {isProcessing && <div className="flex justify-start" data-id="element-451">
              <div className="bg-white border border-gray-200 p-3 rounded-lg" data-id="element-452">
                <div className="flex items-center space-x-2" data-id="element-453">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-454"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-75" data-id="element-455"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150" data-id="element-456"></div>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <div className="border border-gray-300 rounded-lg overflow-hidden flex" data-id="element-457">
        <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message..." className="flex-1 p-3 focus:outline-none resize-none" rows={3} disabled={isProcessing} data-id="element-458" />
        <button onClick={handleSendMessage} disabled={!input.trim() || isProcessing} className={`px-4 flex items-center justify-center ${!input.trim() || isProcessing ? 'text-gray-400 bg-gray-100' : 'text-blue-600 hover:bg-blue-50'}`} data-id="element-459">
          <Send className="w-5 h-5" data-id="element-460" />
        </button>
      </div>
    </div>;
};
export default DataSourceAgent;